// ════════════════════════════════════════════════════════════════════════════
// 🎯 CustomerDirectory — Phase 3.4 Full-screen customer roster view (#customers)
// ════════════════════════════════════════════════════════════════════════════
// Lists all customers for a store, with search, sort, KPIs, and detail drawer.
// Mounted by POSDashboard when currentView === 'customers'.
// ════════════════════════════════════════════════════════════════════════════

import React, { useEffect, useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import {
  ArrowLeft, Search, Users, Award, TrendingUp, Phone, User, Mail,
  Calendar, Loader2, Trash2, Edit3, X, Save, BarChart3, Sparkles, UserPlus, DollarSign,
} from 'lucide-react';
import { Haptics } from '../../utils/haptics';
import { confirmAction } from './ConfirmModal';
import type { CustomerLite } from './CustomerPicker';

interface CustomerDirectoryProps {
  storeApiKey: string;
  onBack: () => void;
}

const formatRs = (n: number) =>
  'Rs. ' + n.toLocaleString('en-LK', { minimumFractionDigits: 0, maximumFractionDigits: 0 });

const formatDate = (s?: string | null) => {
  if (!s) return '—';
  try {
    return new Date(s).toLocaleDateString('en-LK', { day: 'numeric', month: 'short', year: 'numeric' });
  } catch { return '—'; }
};

type SortKey = 'recent' | 'spend' | 'points' | 'name';

const CustomerDirectory: React.FC<CustomerDirectoryProps> = ({ storeApiKey, onBack }) => {
  const [customers, setCustomers] = useState<CustomerLite[]>([]);
  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState('');
  const [sort, setSort] = useState<SortKey>('recent');
  const [selected, setSelected] = useState<CustomerLite | null>(null);
  const [editForm, setEditForm] = useState<Partial<CustomerLite> | null>(null);
  const [savingEdit, setSavingEdit] = useState(false);
  const [history, setHistory] = useState<any[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  // 🛡️ Phase 4 — Unified RBAC Headers Helper
  const getHeaders = () => {
    const token = localStorage.getItem('kangavena:cashierToken') || '';
    return {
      'x-store-api-key': storeApiKey,
      'Content-Type': 'application/json',
      ...(token ? { 'Authorization': `Bearer ${token}` } : {})
    };
  };

  // 📝 Create Customer States
  const [showAddModal, setShowAddModal] = useState(false);
  const [addForm, setAddForm] = useState({
    name: '',
    phone: '',
    email: '',
    birthday: '',
    notes: ''
  });
  const [savingAdd, setSavingAdd] = useState(false);

  const saveAdd = async () => {
    if (!addForm.name || !addForm.phone) {
      alert('Name and phone are required');
      return;
    }
    setSavingAdd(true);
    try {
      const res = await fetch('/api/customers', {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify(addForm),
      });
      const data = await res.json();
      if (res.ok) {
        Haptics.fire('success');
        setAddForm({ name: '', phone: '', email: '', birthday: '', notes: '' });
        setShowAddModal(false);
        load();
      } else {
        Haptics.fire('warn');
        alert(data.error || 'Failed to add customer');
      }
    } catch {
      alert('Failed to add customer. Check your network.');
    } finally {
      setSavingAdd(false);
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      const res = await fetch(
        `/api/customers?q=${encodeURIComponent(query)}&sort=${sort}&limit=200`,
        { headers: getHeaders() }
      );
      const data = await res.json();
      if (res.ok) setCustomers(data.customers || []);
    } catch { /* network */ }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [sort]);
  useEffect(() => {
    const t = window.setTimeout(load, 250);
    return () => window.clearTimeout(t);
    /* eslint-disable-next-line */
  }, [query]);

  // Aggregate KPIs
  const kpis = useMemo(() => {
    const totalSpent = customers.reduce((s, c) => s + Number(c.totalSpent), 0);
    const totalPoints = customers.reduce((s, c) => s + Number(c.points), 0);
    const totalVisits = customers.reduce((s, c) => s + Number(c.totalVisits), 0);
    const totalStoreCredit = customers.reduce((s, c) => s + Number(c.storeCredit || 0), 0);
    const customersWithCredit = customers.filter(c => Number(c.storeCredit || 0) > 0).length;
    return { count: customers.length, totalSpent, totalPoints, totalVisits, totalStoreCredit, customersWithCredit };
  }, [customers]);

  const openDetail = async (c: CustomerLite) => {
    setSelected(c);
    setEditForm(null);
    setHistory([]);
    setHistoryLoading(true);
    try {
      const res = await fetch(`/api/customers/${c.id}`, { headers: getHeaders() });
      const data = await res.json();
      if (res.ok) {
        setSelected(data.customer);
        setHistory(data.transactions || []);
      }
    } catch { /* silent */ }
    finally { setHistoryLoading(false); }
  };

  const beginEdit = () => {
    if (!selected) return;
    setEditForm({
      name: selected.name,
      phone: selected.phone,
      email: selected.email || '',
      birthday: selected.birthday || '',
      notes: selected.notes || '',
    });
  };

  const saveEdit = async () => {
    if (!selected || !editForm) return;
    setSavingEdit(true);
    try {
      const res = await fetch(`/api/customers/${selected.id}`, {
        method: 'PATCH',
        headers: getHeaders(),
        body: JSON.stringify(editForm),
      });
      const data = await res.json();
      if (res.ok) {
        Haptics.fire('success');
        setSelected(data.customer);
        setEditForm(null);
        load();
      }
    } catch { /* silent */ }
    finally { setSavingEdit(false); }
  };

  const removeCustomer = async () => {
    if (!selected) return;
    const ok = await confirmAction({
      title: `Remove ${selected.name}?`,
      message: 'Their loyalty balance will be lost. Past transactions will be retained but unlinked.',
      confirmLabel: 'Remove',
      destructive: true,
    });
    if (!ok) return;
    try {
      const res = await fetch(`/api/customers/${selected.id}`, {
        method: 'DELETE',
        headers: getHeaders()
      });
      if (res.ok) {
        Haptics.fire('warn');
        setSelected(null);
        load();
      }
    } catch { /* silent */ }
  };

  return (
    <div className="fixed inset-0 z-[60] bg-gradient-to-br from-sky-50 via-white to-slate-50 flex flex-col">
      {/* Header */}
      <div className="shrink-0 flex items-center justify-between px-5 py-3 border-b border-slate-200 bg-white/80 backdrop-blur-md">
        <div className="flex items-center gap-2">
          <button
            onClick={onBack}
            className="w-9 h-9 rounded-xl bg-slate-50 hover:bg-slate-100 border border-slate-200 flex items-center justify-center text-slate-600 transition-colors"
            aria-label="Back"
          >
            <ArrowLeft size={15} />
          </button>
          <div>
            <h2 className="text-[15px] font-black text-slate-800 leading-tight flex items-center gap-2">
              <Users size={15} className="text-brand-primary" /> Customers
            </h2>
            <p className="text-[10px] text-slate-500">Loyalty &amp; CRM directory</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => setShowAddModal(true)}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-gradient-to-r from-emerald-500 to-emerald-600 text-white text-[11px] font-black uppercase tracking-wider shadow-md hover:opacity-90 active:scale-95 transition-all"
          >
            <UserPlus size={13} /> Add Customer
          </button>
          <div className="text-[10px] text-slate-500 font-mono hidden sm:block">{kpis.count} total</div>
        </div>
      </div>

      {/* KPIs */}
      <div className="shrink-0 grid grid-cols-2 sm:grid-cols-4 gap-2 p-4">
        <KpiCard icon={Users} label="Customers" value={kpis.count.toString()} tint="sky" />
        <KpiCard icon={TrendingUp} label="Lifetime Spend" value={formatRs(kpis.totalSpent)} tint="emerald" />
        <KpiCard icon={Award} label="Points Outstanding" value={kpis.totalPoints.toFixed(0)} tint="amber" />
        <KpiCard icon={BarChart3} label="Total Visits" value={kpis.totalVisits.toString()} tint="violet" />
        {kpis.totalStoreCredit > 0 && (
          <KpiCard icon={DollarSign} label="Store Credit" value={formatRs(kpis.totalStoreCredit)} tint="emerald" subtitle={`${kpis.customersWithCredit} customers`} />
        )}
      </div>

      {/* Search + Sort */}
      <div className="shrink-0 px-4 pb-3 flex items-center gap-2">
        <div className="relative flex-1">
          <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search name or phone…"
            className="w-full pl-9 pr-3 py-2 rounded-xl bg-white border border-slate-200 focus:border-brand-primary focus:ring-2 focus:ring-brand-primary/20 outline-none text-[12px] text-slate-800"
          />
        </div>
        <select
          value={sort}
          onChange={(e) => setSort(e.target.value as SortKey)}
          className="py-2 px-3 rounded-xl bg-white border border-slate-200 text-[11px] font-bold text-slate-700 outline-none focus:border-brand-primary"
        >
          <option value="recent">Recent</option>
          <option value="spend">Top Spend</option>
          <option value="points">Top Points</option>
          <option value="name">Name A-Z</option>
        </select>
      </div>

      {/* List */}
      <div className="flex-1 overflow-y-auto px-4 pb-4 space-y-1.5">
        {loading && (
          <div className="flex items-center justify-center py-10 text-slate-400 text-[11px]">
            <Loader2 size={14} className="animate-spin mr-2" /> Loading…
          </div>
        )}

        {!loading && customers.length === 0 && (
          <div className="text-center py-12 text-slate-400 text-[11px] flex flex-col items-center justify-center space-y-4">
            <div className="w-14 h-14 rounded-full bg-slate-100 flex items-center justify-center text-slate-400">
              <Users size={24} className="opacity-60" />
            </div>
            <div>
              <p className="font-bold text-slate-700 text-[12px]">No customers found</p>
              <p className="text-slate-400 max-w-xs mt-1">There are no customers currently registered in your store database.</p>
            </div>
            <button
              onClick={() => setShowAddModal(true)}
              className="flex items-center gap-1.5 px-4 py-2 rounded-xl bg-gradient-to-r from-emerald-500 to-emerald-600 text-white text-[11px] font-black uppercase tracking-wider shadow-md hover:opacity-90 active:scale-95 transition-all"
            >
              <UserPlus size={13} /> Add First Customer
            </button>
          </div>
        )}

        {customers.map((c) => (
          <motion.button
            key={c.id}
            whileTap={{ scale: 0.99 }}
            onClick={() => openDetail(c)}
            className="w-full flex items-center gap-3 p-3 rounded-xl border border-slate-100 bg-white hover:bg-brand-surface hover:border-brand-primary text-left transition-all"
          >
            <div className="w-11 h-11 rounded-full bg-gradient-to-br from-brand-primary-100 to-brand-surface border border-brand-surface-border flex items-center justify-center shrink-0">
              <User size={16} className="text-brand-primary" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[12px] font-black text-slate-800 truncate">{c.name}</div>
              <div className="text-[10px] text-slate-500 font-mono flex items-center gap-2 mt-0.5">
                <span className="flex items-center gap-1"><Phone size={9} /> {c.phone}</span>
                {c.lastVisitAt && <span className="text-slate-300">·</span>}
                {c.lastVisitAt && <span>last {formatDate(c.lastVisitAt)}</span>}
              </div>
            </div>
            <div className="text-right shrink-0">
              <div className="flex items-center gap-1 text-[12px] font-black text-amber-500 justify-end">
                <Award size={11} /> {Number(c.points).toFixed(0)}
              </div>
              <div className="text-[10px] text-slate-500 font-mono">
                {formatRs(Number(c.totalSpent))}
              </div>
              <div className="text-[9px] text-slate-400 font-mono">{c.totalVisits} visits</div>
              {Number(c.storeCredit || 0) > 0 && (
                <div className="text-[9px] font-bold text-emerald-600 font-mono mt-0.5">
                  💰 {formatRs(Number(c.storeCredit))} credit
                </div>
              )}
            </div>
          </motion.button>
        ))}
      </div>

      {/* Detail drawer */}
      <AnimatePresence>
        {selected && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[9998] bg-slate-900/60 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4"
            onClick={(e) => { if (e.target === e.currentTarget) setSelected(null); }}
          >
            <motion.div
              initial={{ y: 400 }} animate={{ y: 0 }} exit={{ y: 400 }}
              transition={{ type: 'spring', stiffness: 380, damping: 32 }}
              className="w-full sm:max-w-xl max-h-[92vh] bg-white rounded-t-3xl sm:rounded-3xl shadow-2xl border border-slate-200 overflow-hidden flex flex-col"
            >
              {/* Drawer header */}
              <div className="shrink-0 flex items-center justify-between px-5 pt-4 pb-3 border-b border-slate-100">
                <div className="flex items-center gap-3">
                  <div className="w-11 h-11 rounded-full bg-gradient-to-br from-brand-primary-100 to-brand-surface border border-brand-surface-border flex items-center justify-center">
                    <User size={16} className="text-brand-primary" />
                  </div>
                  <div>
                    <h3 className="text-[14px] font-black text-slate-800 leading-tight">{selected.name}</h3>
                    <p className="text-[10px] text-slate-500 font-mono flex items-center gap-1.5">
                      <Phone size={9} /> {selected.phone}
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => setSelected(null)}
                  className="w-8 h-8 rounded-lg hover:bg-slate-100 flex items-center justify-center text-slate-400 hover:text-slate-700"
                  aria-label="Close"
                >
                  <X size={15} />
                </button>
              </div>

              {/* Body */}
              <div className="flex-1 overflow-y-auto p-4 space-y-4">
                {/* Stats */}
                <div className="grid grid-cols-3 gap-2">
                  <div className="rounded-xl bg-amber-50 border border-amber-200 p-3 text-center">
                    <Award size={13} className="mx-auto text-amber-500 mb-1" />
                    <div className="text-[16px] font-black text-amber-700 font-mono tabular-nums">
                      {Number(selected.points).toFixed(0)}
                    </div>
                    <div className="text-[9px] text-amber-600 uppercase tracking-widest font-bold">Points</div>
                  </div>
                  <div className="rounded-xl bg-emerald-50 border border-emerald-200 p-3 text-center">
                    <TrendingUp size={13} className="mx-auto text-emerald-500 mb-1" />
                    <div className="text-[12px] font-black text-emerald-700 font-mono tabular-nums">
                      {formatRs(Number(selected.totalSpent))}
                    </div>
                    <div className="text-[9px] text-emerald-600 uppercase tracking-widest font-bold">Lifetime</div>
                  </div>
                  <div className="rounded-xl bg-violet-50 border border-violet-200 p-3 text-center">
                    <Sparkles size={13} className="mx-auto text-violet-500 mb-1" />
                    <div className="text-[16px] font-black text-violet-700 font-mono tabular-nums">
                      {selected.totalVisits}
                    </div>
                    <div className="text-[9px] text-violet-600 uppercase tracking-widest font-bold">Visits</div>
                  </div>
                </div>

                {/* Profile / edit */}
                {editForm ? (
                  <div className="space-y-2.5 rounded-2xl border border-slate-200 p-3">
                    <h4 className="text-[10px] font-black text-slate-500 uppercase tracking-widest">Edit profile</h4>
                    <input
                      value={editForm.name || ''}
                      onChange={(e) => setEditForm((f) => ({ ...f!, name: e.target.value }))}
                      placeholder="Name"
                      className="w-full px-3 py-2 rounded-lg bg-slate-50 border border-slate-200 outline-none focus:border-brand-primary text-[12px]"
                    />
                    <input
                      value={editForm.phone || ''}
                      onChange={(e) => setEditForm((f) => ({ ...f!, phone: e.target.value }))}
                      placeholder="Phone"
                      className="w-full px-3 py-2 rounded-lg bg-slate-50 border border-slate-200 outline-none focus:border-brand-primary text-[12px] font-mono"
                    />
                    <input
                      value={editForm.email || ''}
                      onChange={(e) => setEditForm((f) => ({ ...f!, email: e.target.value }))}
                      placeholder="Email (optional)"
                      className="w-full px-3 py-2 rounded-lg bg-slate-50 border border-slate-200 outline-none focus:border-brand-primary text-[12px]"
                    />
                    <input
                      type="date"
                      value={editForm.birthday || ''}
                      onChange={(e) => setEditForm((f) => ({ ...f!, birthday: e.target.value }))}
                      className="w-full px-3 py-2 rounded-lg bg-slate-50 border border-slate-200 outline-none focus:border-brand-primary text-[12px] font-mono"
                    />
                    <textarea
                      value={editForm.notes || ''}
                      onChange={(e) => setEditForm((f) => ({ ...f!, notes: e.target.value }))}
                      placeholder="Notes"
                      rows={2}
                      className="w-full px-3 py-2 rounded-lg bg-slate-50 border border-slate-200 outline-none focus:border-brand-primary text-[12px] resize-none"
                    />
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => setEditForm(null)}
                        className="flex-1 py-2 rounded-lg bg-slate-50 border border-slate-200 text-slate-600 text-[10px] font-black uppercase"
                      >
                        Cancel
                      </button>
                      <motion.button
                        whileTap={{ scale: 0.97 }}
                        onClick={saveEdit}
                        disabled={savingEdit}
                        className="flex-1 py-2 rounded-lg bg-emerald-500 text-white text-[10px] font-black uppercase disabled:opacity-40 flex items-center justify-center gap-1"
                      >
                        {savingEdit ? <Loader2 size={11} className="animate-spin" /> : <Save size={11} />} Save
                      </motion.button>
                    </div>
                  </div>
                ) : (
                  <div className="rounded-2xl border border-slate-100 bg-slate-50 p-3 space-y-2">
                    <Row icon={Mail} label="Email" value={selected.email || '—'} />
                    <Row icon={Calendar} label="Birthday" value={formatDate(selected.birthday)} />
                    <Row icon={Calendar} label="Last visit" value={formatDate(selected.lastVisitAt)} />
                    <Row icon={Calendar} label="Joined" value={formatDate(selected.createdAt)} />
                    {selected.notes && (
                      <div className="pt-1 border-t border-slate-200">
                        <div className="text-[9px] font-black uppercase tracking-widest text-slate-500 mb-1">Notes</div>
                        <p className="text-[11px] text-slate-700 whitespace-pre-wrap">{selected.notes}</p>
                      </div>
                    )}
                  </div>
                )}

                {/* History */}
                <div>
                  <h4 className="text-[10px] font-black text-slate-500 uppercase tracking-widest mb-2">
                    Recent transactions
                  </h4>
                  {historyLoading ? (
                    <div className="text-center py-6 text-slate-400 text-[11px]">
                      <Loader2 size={12} className="animate-spin inline mr-1" /> Loading…
                    </div>
                  ) : history.length === 0 ? (
                    <div className="text-center py-6 text-slate-400 text-[11px]">No transactions yet.</div>
                  ) : (
                    <div className="space-y-1">
                      {history.slice(0, 12).map((t: any) => (
                        <div
                          key={t.id}
                          className="flex items-center justify-between px-3 py-2 rounded-lg bg-white border border-slate-100 text-[11px]"
                        >
                          <div>
                            <div className="font-bold text-slate-700">{formatRs(Number(t.total))}</div>
                            <div className="text-[9px] text-slate-400 font-mono">
                              {formatDate(t.createdAt)} · {t.paymentMethod}
                              {t.status === 'voided' && <span className="ml-1 text-rose-500">· VOID</span>}
                            </div>
                          </div>
                          <div className="text-right">
                            {Number(t.pointsEarned) > 0 && (
                              <div className="text-[10px] font-bold text-amber-600">+{Number(t.pointsEarned).toFixed(0)} pts</div>
                            )}
                            {Number(t.pointsRedeemed) > 0 && (
                              <div className="text-[10px] font-bold text-violet-600">−{Number(t.pointsRedeemed).toFixed(0)} pts</div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Footer actions */}
              {!editForm && (
                <div className="shrink-0 flex items-center gap-2 px-4 py-3 border-t border-slate-100">
                  <button
                    onClick={removeCustomer}
                    className="flex-1 py-2.5 rounded-xl bg-rose-50 hover:bg-rose-100 border border-rose-200 text-rose-600 text-[11px] font-black uppercase tracking-wider flex items-center justify-center gap-1.5 transition-all"
                  >
                    <Trash2 size={11} /> Remove
                  </button>
                  <button
                    onClick={beginEdit}
                    className="flex-1 py-2.5 rounded-xl bg-brand-surface hover:bg-brand-primary-100 border border-brand-surface-border text-brand-primary text-[11px] font-black uppercase tracking-wider flex items-center justify-center gap-1.5 transition-all"
                  >
                    <Edit3 size={11} /> Edit
                  </button>
                </div>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ➕ CREATE CUSTOMER GLASSMORPHIC MODAL */}
      <AnimatePresence>
        {showAddModal && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] bg-black/60 backdrop-blur-md flex items-center justify-center p-4"
          >
            <motion.div
              initial={{ scale: 0.95, y: 15 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.95, y: 15 }}
              className="w-full max-w-md bg-white border border-slate-100 rounded-3xl overflow-hidden shadow-2xl p-6 space-y-4"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <UserPlus size={16} className="text-emerald-500" />
                  <span className="text-[14px] font-black text-slate-800 uppercase tracking-wider">New Customer Profile</span>
                </div>
                <button
                  onClick={() => setShowAddModal(false)}
                  className="w-7 h-7 rounded-lg hover:bg-slate-100 flex items-center justify-center text-slate-500 hover:text-slate-800 transition-colors"
                >
                  <X size={14} />
                </button>
              </div>

              <div className="space-y-3 text-[11px]">
                {/* Name */}
                <div className="space-y-0.5">
                  <label className="text-[9px] text-slate-400 uppercase tracking-widest font-mono font-bold">Full Name *</label>
                  <input
                    type="text" required
                    value={addForm.name}
                    onChange={e => setAddForm(prev => ({ ...prev, name: e.target.value }))}
                    placeholder="Enter customer's full name…"
                    className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-[12px] font-bold text-slate-800 outline-none focus:border-brand-primary"
                  />
                </div>

                {/* Phone */}
                <div className="space-y-0.5">
                  <label className="text-[9px] text-slate-400 uppercase tracking-widest font-mono font-bold">Phone Number *</label>
                  <input
                    type="tel" required
                    value={addForm.phone}
                    onChange={e => setAddForm(prev => ({ ...prev, phone: e.target.value }))}
                    placeholder="e.g. 0771234567"
                    className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-[12px] font-bold text-slate-800 outline-none focus:border-brand-primary font-mono"
                  />
                </div>

                {/* Email */}
                <div className="space-y-0.5">
                  <label className="text-[9px] text-slate-400 uppercase tracking-widest font-mono font-bold">Email Address</label>
                  <input
                    type="email"
                    value={addForm.email}
                    onChange={e => setAddForm(prev => ({ ...prev, email: e.target.value }))}
                    placeholder="customer@example.com"
                    className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-[12px] font-bold text-slate-800 outline-none focus:border-brand-primary"
                  />
                </div>

                {/* Birthday */}
                <div className="space-y-0.5">
                  <label className="text-[9px] text-slate-400 uppercase tracking-widest font-mono font-bold">Birthday</label>
                  <input
                    type="date"
                    value={addForm.birthday}
                    onChange={e => setAddForm(prev => ({ ...prev, birthday: e.target.value }))}
                    className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-[12px] font-bold text-slate-800 outline-none focus:border-brand-primary font-mono"
                  />
                </div>

                {/* Notes */}
                <div className="space-y-0.5">
                  <label className="text-[9px] text-slate-400 uppercase tracking-widest font-mono font-bold">Special Notes (Preferences, Loyalty status)</label>
                  <textarea
                    rows={2}
                    value={addForm.notes}
                    onChange={e => setAddForm(prev => ({ ...prev, notes: e.target.value }))}
                    placeholder="Add special notes or preferences here…"
                    className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-[12px] font-bold text-slate-800 outline-none focus:border-brand-primary"
                  />
                </div>
              </div>

              <div className="flex gap-2 pt-2">
                <button
                  onClick={() => setShowAddModal(false)}
                  className="flex-1 py-2.5 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-700 text-[11px] font-black uppercase tracking-wider transition-all"
                >
                  Cancel
                </button>
                <button
                  onClick={saveAdd}
                  disabled={savingAdd || !addForm.name || !addForm.phone}
                  className="flex-1 py-2.5 rounded-xl bg-gradient-to-r from-emerald-500 to-emerald-600 text-white text-[11px] font-black uppercase tracking-wider flex items-center justify-center gap-1.5 transition-all shadow-md shadow-emerald-500/10 disabled:opacity-40"
                >
                  {savingAdd ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />} Save Profile
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const KpiCard: React.FC<{ icon: any; label: string; value: string; tint: 'sky' | 'emerald' | 'amber' | 'violet'; subtitle?: string }> = ({
  icon: Icon, label, value, tint, subtitle,
}) => {
  const colors: Record<string, string> = {
    sky: 'from-sky-50 to-white border-sky-200 text-sky-600',
    emerald: 'from-emerald-50 to-white border-emerald-200 text-emerald-600',
    amber: 'from-amber-50 to-white border-amber-200 text-amber-600',
    violet: 'from-violet-50 to-white border-violet-200 text-violet-600',
  };
  return (
    <div className={`rounded-2xl bg-gradient-to-br ${colors[tint]} border p-3`}>
      <div className="flex items-center gap-1.5">
        <Icon size={11} />
        <span className="text-[9px] font-black uppercase tracking-widest">{label}</span>
      </div>
      <div className="text-[15px] font-black text-slate-800 font-mono tabular-nums mt-1 truncate">{value}</div>
      {subtitle && <div className="text-[9px] text-slate-500 font-mono mt-0.5">{subtitle}</div>}
    </div>
  );
};

const Row: React.FC<{ icon: any; label: string; value: string }> = ({ icon: Icon, label, value }) => (
  <div className="flex items-center justify-between text-[11px]">
    <span className="flex items-center gap-1.5 text-slate-500">
      <Icon size={11} /> {label}
    </span>
    <span className="font-mono font-bold text-slate-700">{value}</span>
  </div>
);

export default CustomerDirectory;
